"use client"

// src/components/pages/DriverApp.jsx
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import DashboardTemplate from "../templates/DashboardTemplate"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"

export default function DriverApp() {
  const { orderId } = useParams()
  const { user } = useAuth()
  const [error, setError] = useState(null)
  const [location, setLocation] = useState(null)
  const [isTracking, setIsTracking] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(null)

  useEffect(() => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser")
      return
    }

    // watchPosition will call back whenever the location changes
    const watchId = navigator.geolocation.watchPosition(
      async ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords
        setLocation({ lat, lng })
        setIsTracking(true)
        setLastUpdate(new Date())

        try {
          await api.post(`/delivery/${orderId}/location`, { lat, lng })
        } catch (err) {
          console.error("Failed to send location:", err)
          if (!error) {
            setError("Failed to send location to server. Will keep trying.")
          }
        }
      },
      (err) => {
        console.error("Geolocation error:", err)
        setIsTracking(false)
        setError(
          err.code === 1
            ? "Location access denied. Please enable location services."
            : "Unable to retrieve your location. Please check your device settings.",
        )
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 },
    )

    return () => {
      navigator.geolocation.clearWatch(watchId)
    }
  }, [orderId, error])

  return (
    <DashboardTemplate>
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="bg-amber-600 text-white p-4">
            <h1 className="text-2xl font-bold">Driver Tracking</h1>
            <p className="text-amber-100">Order #{orderId}</p>
          </div>

          <div className="p-6">
            {error ? (
              <div className="p-4 mb-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mb-6">
              <div className="flex items-center mb-4">
                <div
                  className={`w-3 h-3 rounded-full mr-2 ${isTracking ? "bg-green-500" : "bg-gray-300"} ${
                    isTracking ? "animate-pulse" : ""
                  }`}
                ></div>
                <span className="font-medium">{isTracking ? "Tracking active" : "Tracking inactive"}</span>
              </div>

              {location && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Current Location</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Latitude</p>
                      <p className="font-mono">{location.lat.toFixed(6)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Longitude</p>
                      <p className="font-mono">{location.lng.toFixed(6)}</p>
                    </div>
                  </div>
                  {lastUpdate && (
                    <p className="text-xs text-gray-500 mt-2">Last updated: {lastUpdate.toLocaleTimeString()}</p>
                  )}
                </div>
              )}
            </div>

            <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
              <h3 className="text-lg font-medium text-amber-800 mb-2">How it works</h3>
              <ul className="space-y-2 text-sm text-amber-700">
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-amber-500 mr-2 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Your device will automatically send GPS coordinates to the server
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-amber-500 mr-2 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Customers can track their order in real-time
                </li>
                <li className="flex items-start">
                  <svg
                    className="h-5 w-5 text-amber-500 mr-2 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Keep this page open during delivery
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </DashboardTemplate>
  )
}
